import { useSettings } from "../context/SettingsContext";

/**
 * Floating social click-to-chat stack.
 * Rendered globally; each button (WhatsApp, Instagram, Facebook) is independently
 * toggle-able from the admin Site Settings tab.
 */
export default function WhatsAppFloat() {
    const { settings } = useSettings();

    // WhatsApp
    const waRaw = String(settings?.whatsapp_number || "").replace(/[^\d]/g, "");
    const waEnabled = !!settings?.whatsapp_enabled && !!waRaw;
    const waMessage = settings?.whatsapp_default_message || "";
    const waHref = `https://wa.me/${waRaw}${waMessage ? `?text=${encodeURIComponent(waMessage)}` : ""}`;

    // Instagram
    const igUrl = String(settings?.instagram_url || "").trim();
    const igEnabled = !!settings?.instagram_enabled && !!igUrl;
    const igHref = igUrl.startsWith("http") ? igUrl : `https://${igUrl}`;

    // Facebook
    const fbUrl = String(settings?.facebook_url || "").trim();
    const fbEnabled = !!settings?.facebook_enabled && !!fbUrl;
    const fbHref = fbUrl.startsWith("http") ? fbUrl : `https://${fbUrl}`;

    if (!waEnabled && !igEnabled && !fbEnabled) return null;

    return (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-row items-end gap-3">
            {igEnabled && (
                <a
                    href={igHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                    data-testid="instagram-float-button"
                    className="group"
                >
                    <span className="relative flex h-12 w-12 items-center justify-center rounded-full ring-2 ring-[#C29A5B] shadow-[0_8px_24px_rgba(107,21,53,0.25)] transition-transform duration-200 group-hover:scale-105"
                        style={{ background: "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)" }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden>
                            <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41-.56-.22-.96-.48-1.38-.9-.42-.42-.68-.82-.9-1.38-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2M12 0C8.74 0 8.33.01 7.05.07 5.77.13 4.9.33 4.14.63c-.79.31-1.46.72-2.13 1.39C1.35 2.68.94 3.35.63 4.14.33 4.9.13 5.77.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.28.26 2.15.56 2.91.31.79.72 1.46 1.39 2.13.67.67 1.34 1.08 2.13 1.39.76.3 1.63.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.28-.06 2.15-.26 2.91-.56.79-.31 1.46-.72 2.13-1.39.67-.67 1.08-1.34 1.39-2.13.3-.76.5-1.63.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.28-.26-2.15-.56-2.91-.31-.79-.72-1.46-1.39-2.13C21.32 1.35 20.65.94 19.86.63c-.76-.3-1.63-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 105.84 12 6.16 6.16 0 0012 5.84zM12 16a4 4 0 114-4 4 4 0 01-4 4zm6.41-11.85a1.44 1.44 0 11-1.44 1.44 1.44 1.44 0 011.44-1.44z"/>
                        </svg>
                    </span>
                </a>
            )}
            {fbEnabled && (
                <a
                    href={fbHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Facebook"
                    data-testid="facebook-float-button"
                    className="group"
                >
                    <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#1877F2] ring-2 ring-[#C29A5B] shadow-[0_8px_24px_rgba(107,21,53,0.25)] transition-transform duration-200 group-hover:scale-105">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-6 w-6 fill-white" aria-hidden>
                            <path d="M22.68 0H1.32C.59 0 0 .59 0 1.32v21.36C0 23.41.59 24 1.32 24h11.5v-9.29H9.69v-3.62h3.13V8.41c0-3.1 1.89-4.79 4.66-4.79 1.32 0 2.46.1 2.79.14v3.24H18.3c-1.51 0-1.8.72-1.8 1.77v2.32h3.6l-.47 3.62h-3.13V24h6.18c.73 0 1.32-.59 1.32-1.32V1.32C24 .59 23.41 0 22.68 0z"/>
                        </svg>
                    </span>
                </a>
            )}
            {waEnabled && (
                <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Chat with us on WhatsApp"
                    data-testid="whatsapp-float-button"
                    className="relative group"
                >
                    <span className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 group-hover:opacity-80 animate-ping" aria-hidden />
                    <span className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] ring-2 ring-[#C29A5B] shadow-[0_8px_24px_rgba(107,21,53,0.25)] transition-transform duration-200 group-hover:scale-105">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-7 w-7 fill-white" aria-hidden>
                            <path d="M19.11 17.39c-.28-.14-1.65-.81-1.9-.9-.26-.1-.44-.14-.63.14-.18.28-.72.9-.88 1.08-.16.19-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.37-.82-.73-1.38-1.63-1.54-1.91-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.07-.23-.55-.46-.47-.63-.48l-.54-.01c-.19 0-.49.07-.74.35-.26.28-.97.94-.97 2.3 0 1.36.99 2.67 1.13 2.86.14.19 1.95 2.97 4.72 4.17.66.28 1.18.45 1.58.58.66.21 1.27.18 1.74.11.53-.08 1.65-.67 1.88-1.32.23-.65.23-1.2.16-1.32-.07-.12-.25-.19-.53-.33zM16.03 5.33c-5.9 0-10.7 4.79-10.7 10.7 0 1.88.49 3.72 1.43 5.33L5 27l5.83-1.52a10.66 10.66 0 0 0 5.2 1.33h.01c5.9 0 10.7-4.79 10.7-10.7s-4.79-10.78-10.71-10.78zm0 19.59h-.01a8.88 8.88 0 0 1-4.53-1.24l-.32-.19-3.46.9.92-3.36-.21-.34a8.83 8.83 0 0 1-1.36-4.72c0-4.89 3.99-8.88 8.88-8.88 2.38 0 4.61.93 6.29 2.61a8.83 8.83 0 0 1 2.6 6.28c0 4.89-3.99 8.84-8.8 8.94z" />
                        </svg>
                    </span>
                </a>
            )}
        </div>
    );
}
